﻿import { CustomLayerInterface, CustomRenderMethodInput, Map as MaplibreMap } from 'maplibre-gl';
import { mercatorXfromLng, mercatorYfromLat } from '../util/mercator-utils';
import fragmentSource from './runway-stripes.fragment.glsl?raw';
import vertexSource from './runway-stripes.vertex.glsl?raw';

export type RunwayStripeMode = 'none' | 'arrival' | 'departure' | 'both';

export interface RunwayStripeConfig {
    corners: [[number, number], [number, number], [number, number], [number, number]];
    mode: RunwayStripeMode;
    /** Opacity 0–1 (default 0.6) */
    opacity?: number;
    /** Minimum stripe count (default 8) */
    stripeCount?: number;
    arriveColor?: [number, number, number];
    departColor?: [number, number, number];
}

function pointHL(x: number, y: number): [number, number, number, number] {
    const xHi = Math.fround(x);
    const yHi = Math.fround(y);
    return [xHi, yHi, x - xHi, y - yHi];
}

// Invert column-major 4x4 matrix in float64, return eye = inv(M) * [0,0,0,1]
function getEye(m: ArrayLike<number>): [number, number, number, number] {
    const a = [
        [m[0], m[4], m[8], m[12]],
        [m[1], m[5], m[9], m[13]],
        [m[2], m[6], m[10], m[14]],
        [m[3], m[7], m[11], m[15]]
    ];
    const b = [0, 0, 0, 1];
    const aug = a.map((row, i) => [...row, b[i]]);
    for (let col = 0; col < 4; col++) {
        let maxRow = col;
        for (let row = col + 1; row < 4; row++) {
            if (Math.abs(aug[row][col]) > Math.abs(aug[maxRow][col])) {
                maxRow = row;
            }
        }
        [aug[col], aug[maxRow]] = [aug[maxRow], aug[col]];
        const pivot = aug[col][col];
        if (Math.abs(pivot) < 1e-30) {
            return [0, 0, 0, 1];
        }
        for (let row = 0; row < 4; row++) {
            if (row === col) {
                continue;
            }
            const factor = aug[row][col] / pivot;
            for (let k = col; k <= 4; k++) {
                aug[row][k] -= factor * aug[col][k];
            }
        }
        for (let k = col; k <= 4; k++) {
            aug[col][k] /= pivot;
        }
    }
    const eye: [number, number, number, number] = [aug[0][4], aug[1][4], aug[2][4], aug[3][4]];

    const w = eye[3];
    return [eye[0] / w, eye[1] / w, eye[2] / w, 1.0 / w];
}

const MODE_INT: Record<RunwayStripeMode, number> = { none: 0, arrival: 1, departure: 2, both: 3 };
const STRIPE_WIDTH_PX = 150;

// Vertex layout: xHi, yHi, xLo, yLo, u, v, mode, opacity, totalStripes, arrR, arrG, arrB, depR, depG, depB
const FLOATS_PER_VERTEX = 15;
const VERTS_PER_QUAD = 4;
const INDICES_PER_QUAD = 6;

// This layer is used to render stripes on runways to indicate arrival/departure directions. The number of stripes is dynamically adjusted based on the runway's screen size to maintain a consistent(ish) appearance across zoom levels.
// At runway scale we run into float32 precision issues when calculating vertex positions. Built in projectTile won't work. To mitigate this, we use the technique of splitting the eye position into high and low float32 parts, and performing the subtraction in two steps to avoid catastrophic cancellation.
// See open issue: https://github.com/maplibre/maplibre-gl-js/issues/4871, solution is based on zerda-ocm comment.
export class RunwayStripesLayer implements CustomLayerInterface {
    readonly type = 'custom' as const;
    readonly id: string;

    private maxAttributeLocation = 0;
    private map: MaplibreMap | null = null;
    private runways: RunwayStripeConfig[] = [];
    private batch: { vbo: WebGLBuffer; ibo: WebGLBuffer; indexCount: number } | null = null;
    private shaderMap = new Map<
        string,
        {
            prog: WebGLProgram;
            aPos: GLint;
            aUv: GLint;
            aMode: GLint;
            aOpacity: GLint;
            aTotalStripes: GLint;
            aArriveColor: GLint;
            aDepartColor: GLint;
        }
    >();

    private runwayCorners: Array<{ nL: [number, number]; fL: [number, number]; fR: [number, number]; nR: [number, number] }> = [];
    private snapped: number[] = [];
    private lastFloorZoom = -1;

    constructor(id: string, runways: RunwayStripeConfig[] = []) {
        this.id = id;
        this.runways = runways;
    }

    setRunways(runways: RunwayStripeConfig[]) {
        this.runways = runways;
        if (this.map) {
            const gl = this.map.painter.context.gl as WebGL2RenderingContext;
            this.freeBatch(gl);
            this.buildBatch(gl);
            this.map.triggerRepaint();
        }
    }

    onAdd(map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext) {
        this.map = map;
        this.buildBatch(gl);
    }

    render(gl: WebGLRenderingContext | WebGL2RenderingContext, args: CustomRenderMethodInput) {
        if (!this.map || !this.batch || this.batch.indexCount === 0) {
            return;
        }

        // Recalculate stripe count based on runway screen size. (only when zoom level changes by an integer step)
        const floorZoom = Math.floor(this.map.getZoom());
        if (floorZoom !== this.lastFloorZoom) {
            this.lastFloorZoom = floorZoom;
            let dirty = false;
            for (let i = 0; i < this.runways.length; i++) {
                const rwy = this.runways[i];
                const spts = rwy.corners.map((c) => this.map!.project(c as [number, number]));
                const screenPx = Math.max(
                    ...[0, 1, 2, 3].map((j) => {
                        const k = (j + 1) % 4;
                        return Math.hypot(spts[k].x - spts[j].x, spts[k].y - spts[j].y);
                    })
                );
                const raw = Math.max((rwy.stripeCount ?? 8) / 2, screenPx / STRIPE_WIDTH_PX);
                const snapped = Math.round(raw / 2) * 2;
                if (snapped !== this.snapped[i]) {
                    this.snapped[i] = snapped;
                    dirty = true;
                }
            }
            if (dirty) {
                gl.bindBuffer(gl.ARRAY_BUFFER, this.batch.vbo);
                gl.bufferData(gl.ARRAY_BUFFER, this.buildVertexData(), gl.DYNAMIC_DRAW);
            }
        }

        // Compute eye from projection matrix in float64, split into hi/lo
        const eye = getEye(args.defaultProjectionData.mainMatrix);
        const eyeHi: [number, number, number, number] = [
            Math.fround(eye[0]),
            Math.fround(eye[1]),
            Math.fround(eye[2]),
            Math.fround(eye[3])
        ];
        const eyeLo: [number, number, number, number] = [eye[0] - eyeHi[0], eye[1] - eyeHi[1], eye[2] - eyeHi[2], eye[3] - eyeHi[3]];

        const sh = this.getShader(gl, args.shaderData);
        if (!sh) {
            return;
        }

        gl.useProgram(sh.prog);

        gl.uniform4fv(gl.getUniformLocation(sh.prog, 'u_eye_high'), eyeHi);
        gl.uniform4fv(gl.getUniformLocation(sh.prog, 'u_eye_low'), eyeLo);
        gl.uniformMatrix4fv(gl.getUniformLocation(sh.prog, 'u_matrix'), false, args.defaultProjectionData.mainMatrix);

        const STRIDE = FLOATS_PER_VERTEX * Float32Array.BYTES_PER_ELEMENT;
        const F = Float32Array.BYTES_PER_ELEMENT;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.batch.vbo);

        const enable = (loc: GLint, size: number, offset: number) => {
            gl.enableVertexAttribArray(loc);
            gl.vertexAttribPointer(loc, size, gl.FLOAT, false, STRIDE, offset * F);
        };

        enable(sh.aPos, 4, 0);
        enable(sh.aUv, 2, 4);
        enable(sh.aMode, 1, 6);
        enable(sh.aOpacity, 1, 7);
        enable(sh.aTotalStripes, 1, 8);
        enable(sh.aArriveColor, 3, 9);
        enable(sh.aDepartColor, 3, 12);

        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.batch.ibo);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        gl.drawElements(gl.TRIANGLES, this.batch.indexCount, gl.UNSIGNED_SHORT, 0);
    }

    onRemove(_map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext) {
        this.freeBatch(gl);
        for (const { prog } of this.shaderMap.values()) {
            gl.deleteProgram(prog);
        }
        this.shaderMap.clear();
        this.map = null;
    }

    private buildVertexData(): Float32Array {
        const data = new Float32Array(this.runways.length * VERTS_PER_QUAD * FLOATS_PER_VERTEX);
        let offset = 0;

        const vtx = (
            p: [number, number],
            u: number,
            v: number,
            mode: number,
            opacity: number,
            totalStripes: number,
            arr: [number, number, number],
            dep: [number, number, number]
        ) => {
            const [xHi, yHi, xLo, yLo] = pointHL(p[0], p[1]);
            data[offset++] = xHi;
            data[offset++] = yHi;
            data[offset++] = xLo;
            data[offset++] = yLo;
            data[offset++] = u;
            data[offset++] = v;
            data[offset++] = mode;
            data[offset++] = opacity;
            data[offset++] = totalStripes;
            data[offset++] = arr[0];
            data[offset++] = arr[1];
            data[offset++] = arr[2];
            data[offset++] = dep[0];
            data[offset++] = dep[1];
            data[offset++] = dep[2];
        };

        for (let i = 0; i < this.runways.length; i++) {
            const rwy = this.runways[i];
            const { nL, nR, fR, fL } = this.runwayCorners[i];
            const mode = MODE_INT[rwy.mode];
            const opacity = rwy.opacity ?? 0.6;
            const totalStripes = this.snapped[i] ?? (rwy.stripeCount ?? 8) / 2;
            const arr = rwy.arriveColor ?? ([0, 0.4, 1] as [number, number, number]);
            const dep = rwy.departColor ?? ([1.0, 0.85, 0] as [number, number, number]);

            vtx(nL, 0, 0, mode, opacity, totalStripes, arr, dep);
            vtx(nR, 1, 0, mode, opacity, totalStripes, arr, dep);
            vtx(fR, 1, 1, mode, opacity, totalStripes, arr, dep);
            vtx(fL, 0, 1, mode, opacity, totalStripes, arr, dep);
        }

        return data;
    }

    private buildBatch(gl: WebGLRenderingContext | WebGL2RenderingContext) {
        this.runwayCorners = [];
        this.snapped = this.runways.map((rwy) => (rwy.stripeCount ?? 8) / 2);

        for (const rwy of this.runways) {
            const mc = rwy.corners.map(toMerc) as [[number, number], [number, number], [number, number], [number, number]];
            const edgeLens = [0, 1, 2, 3].map((i) => {
                const j = (i + 1) % 4;
                return Math.hypot(mc[j][0] - mc[i][0], mc[j][1] - mc[i][1]);
            });
            const longestIdx = edgeLens.indexOf(Math.max(...edgeLens));
            const rot = (i: number) => mc[(longestIdx + i) % 4];
            this.runwayCorners.push({ nL: rot(0), fL: rot(1), fR: rot(2), nR: rot(3) });
        }

        const verts = this.buildVertexData();

        const idxData = new Uint16Array(this.runways.length * INDICES_PER_QUAD);
        for (let i = 0; i < this.runways.length; i++) {
            const base = i * VERTS_PER_QUAD;
            const off = i * INDICES_PER_QUAD;
            idxData[off] = base;
            idxData[off + 1] = base + 1;
            idxData[off + 2] = base + 2;
            idxData[off + 3] = base;
            idxData[off + 4] = base + 2;
            idxData[off + 5] = base + 3;
        }

        const vbo = gl.createBuffer()!;
        gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
        gl.bufferData(gl.ARRAY_BUFFER, verts, gl.DYNAMIC_DRAW);

        const ibo = gl.createBuffer()!;
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idxData, gl.STATIC_DRAW);

        this.batch = { vbo, ibo, indexCount: idxData.length };
        this.lastFloorZoom = -1;
    }

    private freeBatch(gl: WebGLRenderingContext | WebGL2RenderingContext) {
        if (this.batch) {
            // TODO there must be a better way of doing this
            // There are a bunch of attributes enabled and if we delete buffers there's nothing bound to those attributes
            // Figure out how to correctly do this
            for (let i = 0; i <= this.maxAttributeLocation; i++) {
                gl.disableVertexAttribArray(i);
            }
            gl.deleteBuffer(this.batch.vbo);
            gl.deleteBuffer(this.batch.ibo);
            this.batch = null;
        }
        this.runwayCorners = [];
        this.snapped = [];
        this.lastFloorZoom = -1;
    }

    private getShader(gl: WebGLRenderingContext | WebGL2RenderingContext, sd: CustomRenderMethodInput['shaderData']) {
        if (this.shaderMap.has(sd.variantName)) {
            return this.shaderMap.get(sd.variantName)!;
        }

        const vs = gl.createShader(gl.VERTEX_SHADER)!;
        gl.shaderSource(vs, vertexSource);
        gl.compileShader(vs);
        if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) {
            console.error('[RunwayStripes] VS error:', gl.getShaderInfoLog(vs));
        }

        const fs = gl.createShader(gl.FRAGMENT_SHADER)!;
        gl.shaderSource(fs, fragmentSource);
        gl.compileShader(fs);
        if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
            console.error('[RunwayStripes] FS error:', gl.getShaderInfoLog(fs));
        }

        const prog = gl.createProgram()!;
        gl.attachShader(prog, vs);
        gl.attachShader(prog, fs);
        gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
            console.error('[RunwayStripes] link error:', gl.getProgramInfoLog(prog));
        }

        const entry = {
            prog,
            aPos: gl.getAttribLocation(prog, 'a_pos'),
            aUv: gl.getAttribLocation(prog, 'a_uv'),
            aMode: gl.getAttribLocation(prog, 'a_mode'),
            aOpacity: gl.getAttribLocation(prog, 'a_opacity'),
            aTotalStripes: gl.getAttribLocation(prog, 'a_total_stripes'),
            aArriveColor: gl.getAttribLocation(prog, 'a_arrive_color'),
            aDepartColor: gl.getAttribLocation(prog, 'a_depart_color')
        };

        this.maxAttributeLocation = Math.max(
            entry.aPos,
            entry.aUv,
            entry.aMode,
            entry.aOpacity,
            entry.aTotalStripes,
            entry.aArriveColor,
            entry.aDepartColor
        );
        this.shaderMap.set(sd.variantName, entry);
        return entry;
    }
}

function toMerc([lng, lat]: [number, number]): [number, number] {
    return [mercatorXfromLng(lng), mercatorYfromLat(lat)];
}
